import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from './ui/button';
import RegistrationModal from './RegistrationModal';

const PricingSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };
  const plans = [
    {
      name: 'Basic',
      price: '45',
      priceNote: '+ Federal Filing Fee',
      description: 'Essential trademark protection for startups',
      features: [
        'Case Review',
        'Basic Trademark Search',
        'Trademark Monitoring Services (1 Month)',
        'Trademark Secured®',
        'Complete Paperwork'
      ],
      popular: false
    },
    {
      name: 'Deluxe',
      price: '149',
      priceNote: '+ Federal Filing Fee',
      description: 'Enhanced protection with expert review',
      features: [
        'Case Review by a TM Expert',
        'Basic Trademark Search',
        'Trademark Monitoring Services (1 Month)',
        'Trademark Secured®',
        'Refusal Risk Meter®',
        'Complete Paperwork'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: '399',
      priceNote: '+ Federal Filing Fee',
      description: 'Comprehensive protection with priority service',
      features: [
        'Case Review & Filing by Paralegal Officer',
        'Expedited Application Processing within 24 Hours',
        'Filing along with Complete Paperwork',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured®',
        'Refusal Risk Meter®',
        'Litigant Scan',
        'Comprehensive Trademark Search Report (50 States + social media/domains)',
        'Cease & Desist Letter',
        'Dedicated Account Manager'
      ],
      popular: false
    },
    {
      name: 'Signature',
      price: '595',
      priceNote: 'Starting From',
      description: 'Premium service with U.S. Registered IP Attorney',
      features: [
        'Case Review & Filing by U.S Registered IP Attorney',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Cease & Desist Letter',
        'Same Day Filing',
        'Trademark Monitoring (3 Months)',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
        '*(Govt. filing fee not included)'
      ],
      popular: false
    }
  ];

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Pricing Plans
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Smart, Affordable Trademark Protection Tailored to Your Business
          </h2>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 border-2 ${plan.popular ? 'border-[#FF035B] shadow-2xl transform scale-105' : 'border-gray-200'} flex flex-col group transition-all duration-300 hover:border-[#FF035B] hover:shadow-[0_10px_40px_rgba(255,3,91,0.3)] hover:-translate-y-2`}
            >
              {plan.popular && (
                <div className="bg-[#FF035B] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4 text-center">
                  Most Popular
                </div>
              )}
              <h3 className="text-lg font-bold text-black mb-2 transition-colors duration-300 group-hover:text-[#FF035B]">{plan.name}</h3>
              <p className="text-gray-600 mb-4 text-sm min-h-[40px]">{plan.description}</p>
              <div className="mb-4">
                <span className="text-4xl font-bold text-black transition-colors duration-300 group-hover:text-[#FF035B]">${plan.price}</span>
                <p className="text-sm text-gray-600 mt-1">{plan.priceNote}</p>
              </div>
              <button 
                onClick={() => openModal('registration')}
                className={`w-full py-3 text-sm font-semibold mb-6 rounded-lg transition-all duration-300 ${plan.popular ? 'bg-[#FF035B] hover:bg-[#2F1A8E] text-white' : 'bg-[#FF035B] hover:bg-[#2F1A8E] text-white transform hover:scale-105'}`}
              >
                Register My Trademark
              </button>
              <ul className="space-y-3 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF035B] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm leading-tight">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile Stacked Cards */}
        <div className="md:hidden space-y-4">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-5 border-2 ${plan.popular ? 'border-[#FF035B] shadow-xl' : 'border-gray-200'} flex flex-col`}
            >
              {plan.popular && (
                <div className="bg-[#FF035B] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3 text-center w-fit">
                  Most Popular
                </div>
              )}
              <h3 className="text-lg font-bold text-black mb-1">{plan.name}</h3>
              <p className="text-gray-600 mb-3 text-sm">{plan.description}</p>
              <div className="mb-3">
                <span className="text-3xl font-bold text-black">${plan.price}</span>
                <p className="text-xs text-gray-600 mt-1">{plan.priceNote}</p>
              </div>
              <button 
                onClick={() => openModal('registration')}
                className="w-full py-2.5 text-sm font-semibold mb-4 rounded-lg bg-[#FF035B] hover:bg-[#2F1A8E] text-white transition-all"
              >
                Register My Trademark
              </button>
              <ul className="space-y-2 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF035B] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-xs leading-tight">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default PricingSection;